/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useContext,
  createContext,
  cloneElement,
  isValidElement,
} from "react";
import styled from "styled-components";
import { useEscapeKey } from "../../hooks/a11y/useEscapeKey";

const PLACEMENTS = ["top", "bottom", "left", "right", "top-left", "top-right", "bottom-left", "bottom-right"];
const OFFSET = 8;
const VIEWPORT_PADDING = 8;

const getFlippedPlacement = (placement) => {
  const flipMap = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left",
    "top-left": "bottom-left",
    "top-right": "bottom-right",
    "bottom-left": "top-left",
    "bottom-right": "top-right",
  };
  return flipMap[placement] || placement;
};

const calculatePlacementCoordinates = (placement, triggerRect, tooltipRect) => {
  let top, left;

  switch (placement) {
    case "top":
      top = triggerRect.top - tooltipRect.height - OFFSET;
      left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
      break;
    case "bottom":
      top = triggerRect.bottom + OFFSET;
      left = triggerRect.left + (triggerRect.width - tooltipRect.width) / 2;
      break;
    case "left":
      top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
      left = triggerRect.left - tooltipRect.width - OFFSET;
      break;
    case "right":
      top = triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;
      left = triggerRect.right + OFFSET;
      break;
    case "top-left":
      top = triggerRect.top - tooltipRect.height - OFFSET;
      left = triggerRect.left;
      break;
    case "top-right":
      top = triggerRect.top - tooltipRect.height - OFFSET;
      left = triggerRect.right - tooltipRect.width;
      break;
    case "bottom-left":
      top = triggerRect.bottom + OFFSET;
      left = triggerRect.left;
      break;
    case "bottom-right":
      top = triggerRect.bottom + OFFSET;
      left = triggerRect.right - tooltipRect.width;
      break;
    default:
      top = 0;
      left = 0;
  }

  return { top, left };
};

const shouldFlip = (placement, coords, tooltipRect) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const isTopPlacement = placement.startsWith("top");
  const isBottomPlacement = placement.startsWith("bottom");
  const isLeftPlacement = placement === "left";
  const isRightPlacement = placement === "right";

  if (isTopPlacement && coords.top < VIEWPORT_PADDING) {
    return true;
  }
  if (isBottomPlacement && coords.top + tooltipRect.height > viewportHeight - VIEWPORT_PADDING) {
    return true;
  }
  if (isLeftPlacement && coords.left < VIEWPORT_PADDING) {
    return true;
  }
  if (isRightPlacement && coords.left + tooltipRect.width > viewportWidth - VIEWPORT_PADDING) {
    return true;
  }

  return false;
};

const clampToViewport = (coords, tooltipRect) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  return {
    top: Math.min(
      Math.max(coords.top, VIEWPORT_PADDING),
      viewportHeight - tooltipRect.height - VIEWPORT_PADDING
    ),
    left: Math.min(
      Math.max(coords.left, VIEWPORT_PADDING),
      viewportWidth - tooltipRect.width - VIEWPORT_PADDING
    ),
  };
};

const TooltipContext = createContext();

const StyledTooltipContent = styled.div`
  position: fixed;
  width: max-content;
  padding: 6px 8px;
  border: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  border-radius: 4px;
  background: rgba(${({ theme }) => theme.colors.surface.rgba});
  color: rgba(${({ theme }) => theme.colors.textSecondary.rgba});
  font-size: 14px;
  z-index: 1000;
  opacity: ${({ $show }) => ($show ? 1 : 0)};
  visibility: ${({ $show }) => ($show ? "visible" : "hidden")};
  transition: opacity 0.2s, visibility 0.2s;
  pointer-events: none;
  ${({ $coordinates }) => {
    if (!$coordinates) return "";
    const { top, left } = $coordinates;
    return `top: ${top}px; left: ${left}px;`;
  }}
`;

const Tooltip = ({ children, placement = "top" }) => {
  if (!PLACEMENTS.includes(placement)) {
    throw new Error(`Invalid placement: ${placement}. Must be one of ${PLACEMENTS.join(", ")}`);
  }

  const [show, setShow] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);

  useEscapeKey(() => setShow(false), show);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();

    let coords = calculatePlacementCoordinates(placement, triggerRect, tooltipRect);

    if (shouldFlip(placement, coords, tooltipRect)) {
      const flippedPlacement = getFlippedPlacement(placement);
      coords = calculatePlacementCoordinates(flippedPlacement, triggerRect, tooltipRect);
    }

    const clampedCoords = clampToViewport(coords, tooltipRect);

    setCoordinates({
      top: Math.round(clampedCoords.top),
      left: Math.round(clampedCoords.left),
    });
  }, [placement]);

  useEffect(() => {
    if (show) {
      calculatePosition();
      window.addEventListener("resize", calculatePosition);
      window.addEventListener("scroll", calculatePosition, { passive: true });
    }

    return () => {
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition);
    };
  }, [show, calculatePosition]);

  return (
    <TooltipContext.Provider value={{ show, setShow, triggerRef, tooltipRef, coordinates }}>
      {children}
    </TooltipContext.Provider>
  );
};

const TooltipTrigger = ({ asChild = false, children, ...props }) => {
  const { setShow, triggerRef } = useContext(TooltipContext);

  const handleMouseEnter = useCallback(() => setShow(true), [setShow]);
  const handleMouseLeave = useCallback(() => setShow(false), [setShow]);

  if (asChild && isValidElement(children)) {
    return cloneElement(children, {
      ref: triggerRef,
      onMouseEnter: (e) => {
        children.props.onMouseEnter?.(e);
        handleMouseEnter(e);
      },
      onMouseLeave: (e) => {
        children.props.onMouseLeave?.(e);
        handleMouseLeave(e);
      },
    });
  }

  return (
    <span ref={triggerRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>
      {children}
    </span>
  );
};

const TooltipContent = ({ children, ...props }) => {
  const { show, tooltipRef, coordinates } = useContext(TooltipContext);

  return (
    <StyledTooltipContent ref={tooltipRef} $show={show} $coordinates={coordinates} {...props}>
      {children}
    </StyledTooltipContent>
  );
};

export { Tooltip, TooltipTrigger, TooltipContent };
